// @file Action history manager
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { safeSessionStorage } from '@@/bits/safe_storage'
import type { DraftPost, Post } from '@@/types'
import useLogger from '@@/vuecomposables/logger'
import { uniqueId } from 'es-toolkit/compat'

export const logger = useLogger('ActionHistoryManager', { logLifecycleHooks: false, log: true })

const ACTION_HISTORY_KEY = 'ww_ah'
const DEFAULT_EXPIRATION_TIME = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

enum ActionHistoryType {
  DELETE_DRAFTS = 'DELETE_DRAFTS',
  DELETE_POSTS = 'DELETE_POSTS',
}

interface BaseActionHistoryRecord<T = unknown> {
  id: string
  type: ActionHistoryType
  timestamp: number
  expiresAt: number
  data: T
}

interface DeleteDraftsActionHistory extends BaseActionHistoryRecord<DraftPost[]> {
  type: ActionHistoryType.DELETE_DRAFTS
}

interface DeletePostsActionHistory extends BaseActionHistoryRecord<Post[]> {
  type: ActionHistoryType.DELETE_POSTS
}

type ActionHistoryRecord = DeleteDraftsActionHistory | DeletePostsActionHistory

interface ActionHistory {
  undoStack: ActionHistoryRecord[]
  redoStack: ActionHistoryRecord[]
}

const isValidActionHistoryRecord = (action: any): action is ActionHistoryRecord => {
  if (action === null || typeof action !== 'object') return false

  // properties must be present
  if (!('type' in action) || !('timestamp' in action) || !('data' in action) || !('expiresAt' in action)) return false

  // type must be valid
  if (!Object.values(ActionHistoryType).includes(action.type)) return false

  // timestamps must be numbers
  if (typeof action.timestamp !== 'number' || typeof action.expiresAt !== 'number') return false

  // must not have expired
  if (action.expiresAt < Date.now()) return false

  return true
}

/**
 * Get the action history from the session storage. This will filter out invalid and expired actions.
 * Using this guarantees that the provided action history is valid and actionable.
 * @returns The action history
 */
const getActionHistory = (): ActionHistory => {
  const stored = safeSessionStorage.getItem(ACTION_HISTORY_KEY)
  if (stored !== null && stored !== '') {
    try {
      const history = JSON.parse(stored)

      // Filter out expired actions
      const validUndoActions = history.undoStack.filter(isValidActionHistoryRecord)
      const validRedoActions = history.redoStack.filter(isValidActionHistoryRecord)

      // If actions were filtered out, save the updated history
      if (validUndoActions.length < history.undoStack.length || validRedoActions.length < history.redoStack.length) {
        history.undoStack = validUndoActions
        history.redoStack = validRedoActions
        saveActionHistory(history)
      }

      return history
    } catch (e) {
      logger.error('Failed to parse action history:', e)
    }
  }
  return { undoStack: [], redoStack: [] }
}

const saveActionHistory = (history: ActionHistory): void => {
  safeSessionStorage.setItem(ACTION_HISTORY_KEY, asciiSafeStringify(history))
}

/**
 * Records a new action in the undo stack and clears the redo stack
 * This is the standard behavior when a user performs a new action
 * @param type The type of action to record
 * @param data The data associated with the action
 */
const recordActionHistory = (type: ActionHistoryType, data: ActionHistoryRecord['data']): string => {
  const history = getActionHistory()

  // When a new action is performed, clear the redo stack
  history.redoStack = []

  const newAction = createActionHistory(type, data)

  history.undoStack.push(newAction)

  saveActionHistory(history)

  return newAction.id
}

/**
 * Records a new action in the undo stack without clearing the redo stack
 * This is used specifically for redo operations to maintain the redo stack
 * @param type The type of action to record
 * @param data The data associated with the action
 */
const recordRedoActionHistory = (type: ActionHistoryType, data: ActionHistoryRecord['data'], id?: string): void => {
  const history = getActionHistory()
  const newAction = createActionHistory(type, data, id)

  history.undoStack.push(newAction)

  saveActionHistory(history)
}

/**
 * Undoes the last action in the undo stack and records it in the redo stack
 * @returns The action that was undone
 */
const undo = (): ActionHistoryRecord | null => {
  const history = getActionHistory()
  if (history.undoStack.length === 0) return null
  const action = history.undoStack.pop() as ActionHistoryRecord
  // refresh the action's expiration time
  history.redoStack.push(createActionHistory(action.type, action.data, action.id))
  saveActionHistory(history)
  return action
}

/**
 * Redoes the last action in the redo stack
 * Does not record the action in the undo stack, use recordRedoActionHistory for that
 * @returns The action that was redone
 */
const redo = (): ActionHistoryRecord | null => {
  const history = getActionHistory()
  if (history.redoStack.length === 0) return null
  const action = history.redoStack.pop() as ActionHistoryRecord
  saveActionHistory(history)
  return action
}

const clearActionHistory = (): void => {
  saveActionHistory({ undoStack: [], redoStack: [] })
}

const createActionHistory = (
  type: ActionHistoryType,
  data: ActionHistoryRecord['data'],
  id?: string,
): ActionHistoryRecord => {
  const now = Date.now()
  const newAction: ActionHistoryRecord = {
    id: id ?? uniqueId('ah_'),
    type,
    timestamp: now,
    data,
    expiresAt: now + DEFAULT_EXPIRATION_TIME,
  }
  return newAction
}

/**
 * Checks if the data belongs to a delete drafts action history record
 * @param record The delete drafts action history record, checks if the type is DELETE_DRAFTS
 * @param actionHistoryData The data to check
 * @returns True if the data belongs to the delete drafts action history record, false otherwise
 */
const isDeleteDraftsActionHistoryRecord = (
  record: ActionHistoryRecord,
  actionHistoryData: ActionHistoryRecord['data'],
): boolean => {
  if (record.type !== ActionHistoryType.DELETE_DRAFTS) return false
  return record.data.every((draft) => actionHistoryData.some((d) => d.cid === draft.cid))
}

/**
 * Checks if the data belongs to a delete posts action history record
 * @param record The delete posts action history record, checks if the type is DELETE_POSTS
 * @param actionHistoryData The data to check
 * @returns True if the data belongs to the delete posts action history record, false otherwise
 */
const isDeletePostsActionHistoryRecord = (
  record: ActionHistoryRecord,
  actionHistoryData: ActionHistoryRecord['data'],
): boolean => {
  if (record.type !== ActionHistoryType.DELETE_POSTS) return false
  return record.data.every((post) => actionHistoryData.some((p) => p.cid === post.cid))
}

/**
 * Returns a function that checks if a record belongs to an action history record
 * @param actionHistoryType The type of action history record
 * @param actionHistoryData The data to check
 * @returns A function that checks if a record belongs to an action history record
 */
const getActionHistoryRecordSearchFn = (
  actionHistoryType: ActionHistoryType,
  actionHistoryData: ActionHistoryRecord['data'],
): ((record: ActionHistoryRecord) => boolean) => {
  switch (actionHistoryType) {
    case ActionHistoryType.DELETE_DRAFTS:
      return (record) => isDeleteDraftsActionHistoryRecord(record, actionHistoryData)
    case ActionHistoryType.DELETE_POSTS:
      return (record) => isDeletePostsActionHistoryRecord(record, actionHistoryData)
    default:
      return () => false
  }
}

/**
 * Finds an action history record in a stack. Only use this if you need to find a specific action history record.
 * @param stack The stack to search in
 * @param actionHistoryType The type of action history record
 * @param actionHistoryData The data to check
 * @returns The action history record and its index
 */
const findActionHistoryRecordInStack = (
  stack: 'undo' | 'redo',
  actionHistoryType: ActionHistoryType,
  actionHistoryData: ActionHistoryRecord['data'],
): { actionRecord: ActionHistoryRecord | null; index: number } => {
  const searchFn = getActionHistoryRecordSearchFn(actionHistoryType, actionHistoryData)

  const history = getActionHistory()
  const historyStack = stack === 'undo' ? history.undoStack : history.redoStack

  for (let i = historyStack.length - 1; i >= 0; i--) {
    const record = historyStack[i]
    if (record.type === actionHistoryType && isValidActionHistoryRecord(record) && searchFn(record)) {
      return { actionRecord: record, index: i }
    }
  }
  return { actionRecord: null, index: -1 }
}

/**
 * Undoes a SPECIFIC action in the undo stack and records it in the redo stack
 * If you want to undo the latest action, use the undo function
 * @returns The action that was undone
 */
const undoSpecificAction = (
  actionHistoryType: ActionHistoryType,
  actionHistoryData: ActionHistoryRecord['data'],
): ActionHistoryRecord | null => {
  const { actionRecord, index } = findActionHistoryRecordInStack('undo', actionHistoryType, actionHistoryData)
  if (actionRecord === null || index === -1) return null

  const history = getActionHistory()
  history.undoStack.splice(index, 1)
  history.redoStack.push(actionRecord)
  saveActionHistory(history)
  return actionRecord
}

/**
 * Redoes a SPECIFIC action in the redo stack
 * If you want to redo the latest action, use the redo function
 * @returns The action that was redone
 */
const redoSpecificAction = (
  actionHistoryType: ActionHistoryType,
  actionHistoryData: ActionHistoryRecord['data'],
): ActionHistoryRecord | null => {
  const { actionRecord, index } = findActionHistoryRecordInStack('redo', actionHistoryType, actionHistoryData)
  if (actionRecord === null || index === -1) return null

  const history = getActionHistory()
  history.redoStack.splice(index, 1)
  history.undoStack.push(actionRecord)
  saveActionHistory(history)
  return actionRecord
}

export {
  ACTION_HISTORY_KEY,
  ActionHistoryType,
  clearActionHistory,
  createActionHistory,
  DEFAULT_EXPIRATION_TIME,
  getActionHistory,
  recordActionHistory,
  recordRedoActionHistory,
  redo,
  redoSpecificAction,
  undo,
  undoSpecificAction,
}
export type { ActionHistory, ActionHistoryRecord, DeleteDraftsActionHistory, DeletePostsActionHistory }
